import React, { useState } from 'react';
import { Calendar, Users, CheckCircle2, Shield, Compass, Loader2 } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useEmailService } from '../hooks/useEmailSender';
import GroupTrip from '../assets/GroupTrip.webp';

interface GroupTripSectionProps {
	onInquire: () => void;
}

export default function GroupTripSection({ onInquire }: GroupTripSectionProps) {
	const { currentLanguage } = useTranslation();
	const { loading, sendGroupTripWaitlist } = useEmailService();
	const [email, setEmail] = useState('');
	const [website, setWebsite] = useState('');
	const [subscribed, setSubscribed] = useState(false);

	const handleInterestSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!email.trim() || loading) return;
		try {
			const existing = JSON.parse(localStorage.getItem('ee_group_trip_waitlist') || '[]');
			existing.push({ email: email.trim(), date: new Date().toISOString(), trip: 'July 2027' });
			localStorage.setItem('ee_group_trip_waitlist', JSON.stringify(existing));
		} catch (err) {
			console.warn('localStorage error', err);
		}

		await sendGroupTripWaitlist({
			email: email.trim(),
			trip: 'Group Trip — July 2027',
			language: currentLanguage,
			website,
		});

		setSubscribed(true);
	};

	// Localized copy helpers
	const titles: Record<
		string,
		{
			tag: string;
			title: string;
			teaser: string;
			desc: string;
			departureLabel: string;
			departureValue: string;
			groupSizeLabel: string;
			groupSizeValue: string;
			statusLabel: string;
			statusValue: string;
			boxBadge: string;
			boxTitle: string;
			boxDesc: string;
			vipTitle: string;
			vipDesc: string;
			emailPlaceholder: string;
			button: string;
			privacyNote: string;
			contactConcierge: string;
		}
	> = {
		en: {
			tag: 'Exclusive Upcoming Departure',
			title: 'Group Trip — July 2027',
			teaser: 'Watch this space. Group Trip. July 2027.',
			desc: 'We are curating an unforgettable, small-group guided safari expedition across South Africa’s most pristine private reserves. Full itinerary details, lodge allocations, and bookings will be unveiled soon.',
			departureLabel: 'Departure',
			departureValue: 'July 2027',
			groupSizeLabel: 'Group Size',
			groupSizeValue: 'Small & Intimate',
			statusLabel: 'Status',
			statusValue: 'In Curation',
			boxBadge: 'Priority Announcements',
			boxTitle: 'Be First to Receive the Itinerary',
			boxDesc:
				'Leave your email or get in touch directly to receive the complete route schedule and priority booking window as soon as it releases.',
			vipTitle: 'You’re on the VIP list!',
			vipDesc: 'We will contact you the moment the July 2027 Group Trip details go live.',
			emailPlaceholder: 'Enter your email address',
			button: 'Join Priority Waitlist',
			privacyNote: 'No spam. Only trip updates.',
			contactConcierge: 'Contact Concierge →',
		},
		it: {
			tag: 'Partenza Esclusiva in Arrivo',
			title: 'Viaggio di Gruppo — Luglio 2027',
			teaser: 'Restate sintonizzati. Viaggio di Gruppo. Luglio 2027.',
			desc: 'Stiamo curando un’indimenticabile spedizione di safari di piccolo gruppo nelle riserve private più incontaminate del Sudafrica. Dettagli completi sull’itinerario, i lodge e le prenotazioni saranno svelati a breve.',
			departureLabel: 'Partenza',
			departureValue: 'Luglio 2027',
			groupSizeLabel: 'Dimensione Gruppo',
			groupSizeValue: 'Piccolo & Esclusivo',
			statusLabel: 'Stato',
			statusValue: 'In Preparazione',
			boxBadge: 'Annunci Prioritari',
			boxTitle: 'Ricevi l’Itinerario in Anteprima',
			boxDesc:
				'Lascia la tua email o contattaci direttamente per ricevere il programma completo e la finestra di prenotazione prioritaria non appena disponibile.',
			vipTitle: 'Sei nella lista VIP!',
			vipDesc: 'Ti contatteremo non appena i dettagli del Viaggio di Gruppo di Luglio 2027 saranno pubblicati.',
			emailPlaceholder: 'Inserisci il tuo indirizzo email',
			button: 'Unisciti alla Lista d’Attesa',
			privacyNote: 'Nessuno spam. Solo aggiornamenti sul viaggio.',
			contactConcierge: 'Contatta il Concierge →',
		},
		fr: {
			tag: 'Départ Exclusif à Venir',
			title: 'Voyage de Groupe — Juillet 2027',
			teaser: 'Restez à l’écoute. Voyage de Groupe. Juillet 2027.',
			desc: 'Nous préparons une expédition safari guidée en petit groupe à travers les plus belles réserves privées d’Afrique du Sud. Les détails de l’itinéraire, les lodges et les réservations seront bientôt dévoilés.',
			departureLabel: 'Départ',
			departureValue: 'Juillet 2027',
			groupSizeLabel: 'Taille du Groupe',
			groupSizeValue: 'Petit & Intimiste',
			statusLabel: 'Statut',
			statusValue: 'En Préparation',
			boxBadge: 'Annonces Prioritaires',
			boxTitle: 'Soyez les Premiers Informés',
			boxDesc:
				'Laissez votre email ou contactez-nous directement pour recevoir le programme complet et l’accès prioritaire aux réservations dès leur ouverture.',
			vipTitle: 'Vous êtes sur la liste VIP !',
			vipDesc: 'Nous vous contacterons dès que les détails du voyage de groupe de juillet 2027 seront dévoilés.',
			emailPlaceholder: 'Entrez votre adresse email',
			button: 'Rejoindre la Liste Prioritaire',
			privacyNote: 'Aucun spam. Uniquement des actualités du voyage.',
			contactConcierge: 'Contacter le Concierge →',
		},
		de: {
			tag: 'Exklusive Bevorstehende Reise',
			title: 'Gruppenreise — Juli 2027',
			teaser: 'Bleiben Sie dran. Gruppenreise. Juli 2027.',
			desc: 'Wir stellen eine unvergessliche geführte Kleingruppen-Safari durch die unberührtesten Privatschutzgebiete Südafrikas zusammen. Vollständige Reiseroutedetails, Lodges und Buchungen werden in Kürze bekannt gegeben.',
			departureLabel: 'Abreise',
			departureValue: 'Juli 2027',
			groupSizeLabel: 'Gruppengröße',
			groupSizeValue: 'Klein & Exklusiv',
			statusLabel: 'Status',
			statusValue: 'In Vorbereitung',
			boxBadge: 'Prioritätsankündigungen',
			boxTitle: 'Erhalten Sie die Reiseroute zuerst',
			boxDesc:
				'Hinterlassen Sie Ihre E-Mail-Adresse oder kontaktieren Sie uns direkt, um den vollständigen Reiseplan und die bevorzugte Buchungsmöglichkeit zu erhalten, sobald diese verfügbar sind.',
			vipTitle: 'Sie stehen auf der VIP-Liste!',
			vipDesc: 'Wir kontaktieren Sie, sobald die Reisedetails für die Gruppenreise im Juli 2027 feststehen.',
			emailPlaceholder: 'E-Mail-Adresse eingeben',
			button: 'Auf Warteliste eintragen',
			privacyNote: 'Kein Spam. Nur Reise-Updates.',
			contactConcierge: 'Concierge kontaktieren →',
		},
	};

	const langCopy = titles[currentLanguage] || titles.en;

	return (
		<section
			id='group-trip-section'
			className='bg-brand-forest text-earth-cream relative py-16 overflow-hidden'
			aria-label='Exclusive Upcoming Departure'
		>
			{/* Background Atmospheric Imagery */}
			<div className='absolute inset-0 z-0 opacity-15'>
				<img
					src={GroupTrip}
					alt='Group Trip of Mongoose'
					loading='lazy'
					decoding='async'
					className='w-full h-full object-cover object-center filter brightness-75 scale-105'
					referrerPolicy='no-referrer'
				/>
			</div>

			{/* Centered Content Container */}
			<div className='relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6'>
				<span className='font-sans text-[10px] tracking-[0.3em] text-brand-gold font-bold uppercase block'>
					{langCopy.tag}
				</span>

				<h3 className='font-serif text-3xl sm:text-4xl text-white font-medium max-w-2xl mx-auto leading-tight'>
					{langCopy.title}
				</h3>

				<div className='inline-block bg-brand-gold/15 border-l-2 border-brand-gold px-3.5 py-1.5 rounded-r-md'>
					<p className='font-mono text-xs sm:text-sm text-brand-gold-light font-semibold tracking-wide'>
						“{langCopy.teaser}”
					</p>
				</div>

				<p className='font-sans text-xs sm:text-sm text-earth-sand-light max-w-2xl mx-auto font-light leading-relaxed'>
					{langCopy.desc}
				</p>

				{/* Trip Attribute Badges */}
				<div className='flex flex-wrap items-center justify-center gap-3 pt-1'>
					<div className='inline-flex items-center space-x-2 bg-brand-forest-dark/70 border border-brand-gold/25 rounded-full px-4 py-1.5 backdrop-blur-xs'>
						<Calendar className='w-3.5 h-3.5 text-brand-gold' />
						<span className='font-mono text-[10px] text-brand-gold-light uppercase font-semibold'>
							{langCopy.departureLabel}: <span className='text-earth-cream font-normal'>{langCopy.departureValue}</span>
						</span>
					</div>
					<div className='inline-flex items-center space-x-2 bg-brand-forest-dark/70 border border-brand-gold/25 rounded-full px-4 py-1.5 backdrop-blur-xs'>
						<Users className='w-3.5 h-3.5 text-brand-gold' />
						<span className='font-mono text-[10px] text-brand-gold-light uppercase font-semibold'>
							{langCopy.groupSizeLabel}: <span className='text-earth-cream font-normal'>{langCopy.groupSizeValue}</span>
						</span>
					</div>
					<div className='inline-flex items-center space-x-2 bg-brand-forest-dark/70 border border-brand-gold/25 rounded-full px-4 py-1.5 backdrop-blur-xs'>
						<Compass className='w-3.5 h-3.5 text-brand-gold' />
						<span className='font-mono text-[10px] text-brand-gold-light uppercase font-semibold'>
							{langCopy.statusLabel}: <span className='text-brand-gold font-bold'>{langCopy.statusValue}</span>
						</span>
					</div>
				</div>

				{/* Waitlist Form & Action */}
				<div className='pt-2 max-w-xl mx-auto'>
					{subscribed ? (
						<div className='bg-brand-gold/15 border border-brand-gold/40 rounded-lg p-4 flex items-center justify-center space-x-3 text-earth-cream animate-fade-in'>
							<CheckCircle2 className='w-5 h-5 text-brand-gold shrink-0' />
							<div className='text-left'>
								<p className='font-serif text-sm font-semibold text-brand-gold-light'>{langCopy.vipTitle}</p>
								<p className='font-sans text-xs text-earth-sand-light font-light mt-0.5'>{langCopy.vipDesc}</p>
							</div>
						</div>
					) : (
						<form onSubmit={handleInterestSubmit} className='flex flex-col sm:flex-row gap-2.5'>
							{/* Honeypot field for anti-spam protection */}
							<div style={{ display: 'none' }} aria-hidden='true'>
								<input
									type='text'
									name='website'
									value={website}
									onChange={e => setWebsite(e.target.value)}
									tabIndex={-1}
									autoComplete='off'
								/>
							</div>
							<input
								id='email'
								type='email'
								value={email}
								autoComplete='email'
								onChange={e => setEmail(e.target.value)}
								placeholder={langCopy.emailPlaceholder}
								required
								disabled={loading}
								className='flex-1 bg-brand-forest-dark/75 border border-earth-sand/30 rounded-lg px-4 py-3.5 text-earth-cream placeholder:text-earth-sand-light/60 font-sans text-xs sm:text-sm focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all disabled:opacity-50'
							/>
							<button
								type='submit'
								disabled={loading}
								className='bg-brand-gold hover:bg-brand-gold-light text-brand-forest-dark font-sans text-xs tracking-widest uppercase font-bold py-3.5 px-6 rounded-lg cursor-pointer transition-transform active:scale-95 duration-200 shrink-0 shadow-md disabled:opacity-50 flex items-center justify-center space-x-2'
							>
								{loading ? (
									<>
										<Loader2 className='w-4 h-4 animate-spin' />
										<span>...</span>
									</>
								) : (
									<span>{langCopy.button}</span>
								)}
							</button>
						</form>
					)}

					<div className='pt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-sans text-earth-sand-light'>
						<span className='flex items-center space-x-1.5 opacity-80'>
							<Shield className='w-3.5 h-3.5 text-brand-gold' />
							<span>{langCopy.privacyNote}</span>
						</span>
						<span className='text-earth-sand/40 hidden sm:inline'>•</span>
						<button
							type='button'
							onClick={onInquire}
							className='text-brand-gold hover:text-brand-gold-light underline underline-offset-4 cursor-pointer font-medium'
						>
							{langCopy.contactConcierge}
						</button>
					</div>
				</div>
			</div>
		</section>
	);
}
